import { Card, Deck } from "./Card"
import { Player } from "./Player"
import { PonkInstance } from "./PonkInstance"



export class PonkServerInterface {
	websocket      : WebSocket
	ponkGame       : PonkInstance|null  = null
	lastPlayedCard : Card|null          = null
	playerTurn     : string|null        = null
	clientId       : string|null        = null
	matchCode      : string|null        = null
	playerList     : Player[]|null      = null
	playerHand     : Deck|null          = null

	public onMatchCreated  : ((matchCode:string) => void) | null = null
	public onMatchStarted  : ((matchCode:string) => void) | null = null
	public onMatchReset    : ((matchCode:string) => void) | null = null
	public onServerConnect : ((clientid:string, serverVersion:string) => void) | null  = null
	public onMatchJoined   : ((matchCode:string, playerList:Player[]) => void) | null  = null
	public onNextRound     : ((matchCode:string, playerTurn:string) => void) | null    = null
	public onPulledCard    : ((matchCode:string, hand:Deck, card:Card) => void) | null = null
	public onPlayedCard    : ((matchCode:string, hand:Deck, card:Card) => void) | null = null
	public onGameEnded     : ((matchCode:string, winner:Player|null) => void) | null   = null


	constructor (serverUrl : string) {
		let socket = new WebSocket(serverUrl)

		socket.onopen    = this.OnWebSocketOpen.bind(this)
		socket.onerror   = this.OnWebSocketError.bind(this)
		socket.onclose   = this.OnWebSocketClose.bind(this)
		socket.onmessage = this.OnWebSocketMessage.bind(this)
		
		this.websocket = socket
	}


	public createMatch (matchName : string) : void {
		const message : string = JSON.stringify({name:matchName})
		this.websocket.send(`create_match ${message}`)
	}


	public joinMatch (matchCode : string, nickname : string) : void {
		const message : string = JSON.stringify({code:matchCode, nickname:nickname})
		this.websocket.send(`join_match ${message}`)
	}


	public leaveMatch (matchCode : string) : void {
		const message : string = JSON.stringify({code:matchCode})
		this.websocket.send(`leave_match ${message}`)
	}


	public startMatch (matchCode : string) : void {
		const message : string = JSON.stringify({code:matchCode})
		this.websocket.send(`start_match ${message}`)
	}


	public resetMatch (matchCode : string) : void {
		const message : string = JSON.stringify({code:matchCode})
		this.websocket.send(`reset_match ${message}`)
	}


	public playCard (card:Card) : void {
		if (this.ponkGame == null)
			throw new Error("cannot play card when no game is playing")

		// deny playing cards not in players hand
		if (this.ponkGame.hand.cards.find( c => c.Code == card.Code ) == undefined) {
			return
		}

		this.lastPlayedCard = card

		const cardMessage = JSON.stringify({
			code:this.matchCode, 
			card:card, 
			pull:false
		})

		this.websocket.send(`play_card ${cardMessage}`)
	}


	public pullCard () : void {
		if (this.ponkGame == null)
			throw new Error("cannot pull card when no game is playing")

		const cardMessage = JSON.stringify({
			code:this.matchCode, 
			card:null, 
			pull:true
		})

		this.websocket.send(`play_card ${cardMessage}`)
	}


	public GetPonkState () : PonkInstance|null {
		return this.ponkGame
	}


	OnWebSocketOpen (event:Event) : void {
		console.log("opened web socket connection")
		//SetInnerHTML("#connection-status", "connected!")
	}


	OnWebSocketError (event:Event) : void {
		console.log("websocket failed to websocket")
		console.log(event)
	}


	OnWebSocketClose (event:Event) : void {
		console.log("websocket was closed")
	}


	OnWebSocketMessage (message:MessageEvent) : void {		
		const messageData : string = message.data
		const commandEnd  : number = messageData.indexOf(' ', 0)
		if (commandEnd == -1)
			throw new Error("malformed server message")

		const command : string = messageData.slice(0, commandEnd)
		const data    : string = messageData.slice(commandEnd)
		const ddata   : any    = JSON.parse(data)

		console.log(command, ddata)

		this.interpretServerMessage(command, ddata)
	}


	interpretServerMessage (command:string, data:any) : void {
		switch (command) {
			case "welcome":
				this.wsWelcome(data)
				break
			case "match_created":
				this.wsMatchCreated(data)
				break
			case "match_joined":
				this.wsMatchJoined(data)
				break
			case "match_started":
				this.wsMatchStarted(data)
				break
			case "play_response":
				this.wsPlayResponse(data)
				break
			case "next_round":
				this.wsNextRound(data)
				break
			case "game_over":
				this.wsGameOver(data)
				break
			case "match_reset":
				this.wsMatchReset(data)
			default:
				throw new Error("recieved unknown command from server")
		}
	}


	wsWelcome (data : any) : void {
		this.clientId = data.clientId

		if (this.clientId == null)
			throw new Error("Recieved bad client id from server")

		if (this.onServerConnect != null)
			this.onServerConnect(this.clientId, data.serverVersion)
	}


	wsMatchCreated (data : any) : void {
		if (this.onMatchCreated != null)
			this.onMatchCreated(data.code as string)
	}


	wsMatchJoined (data : any) : void {
		this.matchCode  = data.code as string
		this.playerList = (data.players as Player[]).map( (p:Player) => Player.Hydrate(p) )

		if (this.onMatchJoined != null) {
			this.onMatchJoined(this.matchCode, this.playerList)
		}
	}


	wsMatchStarted (data : any) : void {
		if (data.code != this.matchCode)
			return

		this.ponkGame = new PonkInstance(new Deck( [ ] ), Deck.Hydrate(data.hand))
		
		this.ponkGame.hand.Sort()

		if (this.onMatchStarted != null)
			this.onMatchStarted(data.code)

		//this.updatePonkVisuals(this.ponkGame)
	}


	wsPlayResponse (data : any) : void {		
		if (this.ponkGame == null)
			throw new Error("ponk game was not initialized")

		if (data.pulledCard != null) {
			const card = Card.Hydrate(data.pulledCard)
			this.ponkGame.hand.AddCard(card)
			this.ponkGame.hand.Sort()
			if (this.onPulledCard != null)
				this.onPulledCard(this.matchCode!, this.ponkGame.hand, card)
		}

		if (data.couldPlay && data.pulledCard==null) {
			if (this.lastPlayedCard == null)
				throw new Error("recieved play response without playing a card")
			this.ponkGame.hand.RemoveCard(this.lastPlayedCard)
			if (this.onPlayedCard != null)
				this.onPlayedCard(this.matchCode!, this.ponkGame.hand, this.lastPlayedCard)
			this.lastPlayedCard = null
		}
	}


	wsNextRound (data : any) : void {
		if (this.ponkGame == null)
			throw new Error("ponk game was not initialized on next round signal")

		this.playerTurn = data.playerTurn
		
		const nextCard : Card = Card.Hydrate(data.pile)

		this.ponkGame.pile.AddCard(nextCard)

		if (this.onNextRound != null)
			this.onNextRound(this.matchCode!, this.playerTurn!)
	}


	wsGameOver (data : any) : void {
		const winner : Player|null = data.winner
		
		if (this.onGameEnded != null)
			this.onGameEnded(this.matchCode!, winner)
	}


	wsMatchReset (data: any) : void {
		if (data.code != this.matchCode)
			return
		if (this.onMatchReset != null)
			this.onMatchReset(this.matchCode!)
	}


	writeGameMessage (msg:string) : void {
		if (msg.endsWith("\n") == false)
			msg += "\n"

		console.log(msg)
	}
}